export type IntentKind =
  | 'RESEARCH'
  | 'ANALYSIS'
  | 'PLANNING'
  | 'WRITING'
  | 'DATA'
  | 'BUILD'
  | 'COMMUNICATION'
  | 'AUTOMATION'
  | 'GENERAL'

export type IntentQuestion = {
  id: string
  question: string
  reason: string
  suggestion: string
  options: string[]
}

export type IntentAnalysis = {
  kind: IntentKind
  confidence: number
  title: string
  goal: string
  audience: string
  expectedOutput: string
  needsFreshInfo: boolean
  needsExternalAction: boolean
  isRecurring: boolean
  scheduleHint: string | null
  questions: IntentQuestion[]
  summary: {
    understood: string[]
    assumptions: string[]
    outOfScope: string[]
    expectedOutput: string
  }
  workSpec: {
    goal: string
    audience: string
    scope: string
    data: string
    constraints: string
    output: string
    acceptance: string
    dependencies: string
    assumptions: string
    unknowns: string
    approvals: string
    outOfScope: string
  }
  routePlan: {
    steps: string[]
    tools: string
    checkpoints: string
  }
}

const norm = (value: string) =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')

const has = (text: string, pattern: RegExp) => pattern.test(norm(text))

function inferKind(raw: string): IntentKind {
  const scores: Record<IntentKind, number> = {
    RESEARCH: 0,
    ANALYSIS: 0,
    PLANNING: 0,
    WRITING: 0,
    DATA: 0,
    BUILD: 0,
    COMMUNICATION: 0,
    AUTOMATION: 0,
    GENERAL: 0,
  }
  const rules: Array<[IntentKind, RegExp, number]> = [
    ['RESEARCH', /(nghien cuu|tim kiem|tra cuu|benchmark|doi thu|thi truong|chinh sach|su kien)/, 3],
    ['ANALYSIS', /(phan tich|danh gia|so sanh|swot|kiem dinh|nghiem thu|kiem tra)/, 3],
    ['PLANNING', /(ke hoach|lo trinh|roadmap|chien luoc|schedule|lich|trien khai)/, 3],
    ['WRITING', /(soan|viet|email|bao cao|de xuat|noi dung|bai viet|tom tat)/, 3],
    ['DATA', /(excel|xlsx|sheet|bang tinh|csv|du lieu|dashboard|bao cao so lieu)/, 4],
    ['BUILD', /(build|code|lap trinh|website|app|ung dung|mvp|repo|github|deploy|sua loi)/, 4],
    ['COMMUNICATION', /(gui email|gui tin nhan|dang bai|lien he|thong bao|moi hop)/, 4],
    ['AUTOMATION', /(nhan su ai|tu dong|automation|dinh ky|hang ngay|moi ngay|daily|moi .* gio|luc .* gio|bao cao .* gio)/, 6],
  ]
  for (const [kind, pattern, weight] of rules) if (has(raw, pattern)) scores[kind] += weight
  const ranked = Object.entries(scores).sort((a, b) => b[1] - a[1]) as Array<[IntentKind, number]>
  return ranked[0][1] > 0 ? ranked[0][0] : 'GENERAL'
}

function inferOutput(raw: string, kind: IntentKind) {
  if (kind === 'AUTOMATION') return 'Nhân sự AI/automation định kỳ với lịch chạy và báo cáo rõ ràng'
  if (has(raw, /(pptx|powerpoint|slide|thuyet trinh)/)) return 'Bộ slide có thể sử dụng ngay'
  if (has(raw, /(xlsx|excel|google sheet|bang tinh|csv)/)) return 'Bảng dữ liệu/bảng tính có thể sử dụng ngay'
  if (has(raw, /(pdf)/)) return 'Tài liệu PDF hoàn chỉnh'
  if (has(raw, /(email|thu dien tu)/)) return 'Email hoàn chỉnh, sẵn sàng gửi'
  if (has(raw, /(website|app|ung dung|mvp|code|repo|deploy)/)) return 'Sản phẩm/chức năng chạy được và có bằng chứng kiểm tra'
  if (kind === 'RESEARCH') return 'Bản nghiên cứu có nguồn, kết luận và đề xuất hành động'
  if (kind === 'ANALYSIS') return 'Bản phân tích có bằng chứng, kết luận và rủi ro'
  if (kind === 'PLANNING') return 'Kế hoạch hành động rõ bước, ưu tiên, đầu ra và mốc kiểm tra'
  if (kind === 'WRITING') return 'Nội dung hoàn chỉnh, có thể sử dụng ngay'
  if (kind === 'DATA') return 'Dữ liệu được cấu trúc, kiểm tra và trình bày để sử dụng'
  return 'Kết quả hoàn chỉnh theo đúng yêu cầu của người dùng'
}

function inferTitle(raw: string, kind: IntentKind) {
  const clean = raw.trim().replace(/\s+/g, ' ')
  if (clean.length <= 72) return clean
  const label: Record<IntentKind, string> = {
    RESEARCH: 'Nghiên cứu',
    ANALYSIS: 'Phân tích',
    PLANNING: 'Lập kế hoạch',
    WRITING: 'Soạn nội dung',
    DATA: 'Xử lý dữ liệu',
    BUILD: 'Xây dựng sản phẩm',
    COMMUNICATION: 'Thực hiện giao tiếp',
    AUTOMATION: 'Tạo nhân sự AI',
    GENERAL: 'Công việc mới',
  }
  return `${label[kind]} · ${clean.slice(0, 56).trim()}…`
}

function inferAudience(raw: string) {
  const t = norm(raw)
  if (/khach hang/.test(t)) return 'Khách hàng'
  if (/ceo|founder|giam doc/.test(t)) return 'Founder/CEO'
  if (/nhan vien|doi ngu|team/.test(t)) return 'Đội ngũ nội bộ'
  if (/benh vien|duoc si/.test(t)) return 'Người dùng chuyên môn'
  return 'Người giao việc'
}

function inferQuestions(raw: string, kind: IntentKind, scheduleHint: string | null): IntentQuestion[] {
  const clean = raw.trim()
  const answered = new Set(
    Array.from(raw.matchAll(/\[CLARIFICATION:([a-z0-9-]+)\]/gi), match => match[1].toLowerCase()),
  )
  const candidates: IntentQuestion[] = []

  const optionsFor = (id: string, suggestion: string): string[] => {
    const map: Record<string, string[]> = {
      'automation-scope': [
        suggestion,
        'Theo dõi công nghệ nói chung, chỉ chọn các tin có ảnh hưởng lớn.',
        'Tập trung vào startup, sản phẩm số và xu hướng ứng dụng thực tế.',
      ],
      'automation-report': [
        suggestion,
        'Bản tóm tắt 3 phút đọc, chỉ gồm những gì cần biết hôm nay.',
        'Báo cáo chuyên sâu hơn, có xu hướng, tác động và khuyến nghị.',
      ],
      'automation-sources': [
        suggestion,
        'Ưu tiên nguồn quốc tế gốc, sau đó tóm tắt bằng tiếng Việt.',
        'Ưu tiên nguồn Việt Nam uy tín, bổ sung nguồn quốc tế khi cần.',
      ],
      'automation-schedule': [
        suggestion,
        'Chạy mỗi ngày lúc 07:00.',
        'Chạy các ngày làm việc lúc 08:00.',
      ],
      'research-scope': [
        suggestion,
        'Chỉ tập trung vào Việt Nam.',
        'Benchmark Việt Nam với 3–5 thị trường quốc tế tương đồng.',
      ],
      'research-time': [
        suggestion,
        'Chỉ dùng dữ liệu 3 tháng gần nhất.',
        'Dùng 12 tháng gần nhất và so sánh với giai đoạn trước.',
      ],
      'research-depth': [
        suggestion,
        'Bản tóm tắt điều hành 1–2 trang, ưu tiên quyết định.',
        'Bản chuyên sâu có phương pháp, bảng so sánh và phụ lục nguồn.',
      ],
      'plan-horizon': [
        suggestion,
        'Kế hoạch 30 ngày tập trung vào kết quả nhanh.',
        'Kế hoạch 12 tháng chia theo quý.',
      ],
      'plan-constraints': [
        suggestion,
        'Tối ưu tốc độ, có thể đề xuất thêm nguồn lực nếu thật sự cần.',
        'Giữ nguyên đội ngũ và ngân sách hiện tại.',
      ],
      'plan-output': [
        suggestion,
        'Bản điều hành ngắn gọn cho CEO/Founder.',
        'Checklist thực thi theo tuần, có người phụ trách và deadline.',
      ],
      'build-must-have': [
        suggestion,
        'Ưu tiên đăng nhập, giao việc, theo dõi trạng thái và nhận kết quả.',
        'Ưu tiên một luồng duy nhất thật tốt rồi mới mở rộng tính năng.',
      ],
      'build-user': [
        suggestion,
        'Founder/CEO là người dùng chính.',
        'Nhân viên vận hành là người dùng chính.',
      ],
      'build-constraint': [
        suggestion,
        'Ưu tiên ra mắt nhanh trong 2–4 tuần.',
        'Ưu tiên chất lượng và khả năng mở rộng hơn tốc độ.',
      ],
      'writing-audience': [
        suggestion,
        'Viết cho khách hàng phổ thông, dễ hiểu và ít thuật ngữ.',
        'Viết cho người chuyên môn, giữ đủ chi tiết kỹ thuật.',
      ],
      'writing-tone': [
        suggestion,
        'Ngắn gọn, thân thiện và trực tiếp.',
        'Trang trọng, thuyết phục và phù hợp tài liệu chính thức.',
      ],
      'writing-format': [
        suggestion,
        'Bản ngắn có thể gửi ngay.',
        'Bản đầy đủ có tiêu đề, cấu trúc và CTA rõ ràng.',
      ],
      'data-format': [
        suggestion,
        'CSV sạch, dễ nhập sang hệ thống khác.',
        'Dashboard/tóm tắt trực quan kèm dữ liệu chi tiết.',
      ],
      'data-insight': [
        suggestion,
        'Chỉ làm sạch và chuẩn hóa dữ liệu.',
        'Phân tích sâu, tìm bất thường và đề xuất hành động.',
      ],
      'data-rules': [
        suggestion,
        'Giữ nguyên mọi công thức hiện có, chỉ sửa lỗi dữ liệu.',
        'Cho phép đề xuất công thức mới nhưng phải giải thích và kiểm tra.',
      ],
      'goal-detail': [
        suggestion,
        'Tạo một đầu ra ngắn gọn để tôi quyết định nhanh.',
        'Tạo một đầu ra chi tiết có thể giao cho đội ngũ thực hiện.',
      ],
      'fallback-outcome': [
        suggestion,
        'Ưu tiên một kết quả duy nhất có thể dùng ngay.',
        'Ưu tiên một quyết định rõ ràng kèm bước tiếp theo.',
      ],
      'fallback-priority': [
        suggestion,
        'Ưu tiên tốc độ.',
        'Ưu tiên chiều sâu và độ chắc chắn.',
      ],
      'fallback-format': [
        suggestion,
        'Tóm tắt điều hành ngắn gọn.',
        'Bản chi tiết có nguồn và phụ lục.',
      ],
    }
    return (map[id] ?? [suggestion]).slice(0, 3)
  }

  const add = (id: string, question: string, reason: string, suggestion: string) => {
    if (answered.has(id)) return
    if (!candidates.some(item => item.id === id)) {
      candidates.push({ id, question, reason, suggestion, options: optionsFor(id, suggestion) })
    }
  }

  const hasExplicitFormat = has(raw, /(pptx|powerpoint|slide|pdf|excel|xlsx|sheet|csv|email|bao cao|bang|dashboard|file|link)/)
  const hasAudience = has(raw, /(cho toi|cho tôi|khach hang|ceo|founder|giam doc|nhan vien|doi ngu|team|benh vien|duoc si)/)
  const hasTimeWindow = has(raw, /(hom nay|tuan nay|thang nay|quy nay|nam nay|\d+\s*(ngay|tuan|thang|nam)|tu ngay|den ngay|trong .* ngay|trong .* thang)/)
  const hasGeo = has(raw, /(viet nam|ca mau|ha noi|tp\.?\s*hcm|toan quoc|quoc te|global|my|nhat|chau au|khu vuc)/)
  const hasDepth = has(raw, /(chi tiet|ngan gon|tom tat|chuyen sau|swot|top \d+|\d+ tin|\d+ muc|\d+ trang|\d+ tu)/)
  const hasSources = has(raw, /(nguon|website|tap chi|youtube|reddit|google|quoc te|viet nam|uy tin|techcrunch|hackernews|linkedin|bai bao|bao chi)/)
  const hasConstraints = has(raw, /(ngan sach|chi phi|mien phi|zero|khong duoc|bat buoc|gioi han|deadline|thoi han)/)
  const hasFeatures = has(raw, /(tinh nang|must-have|chuc nang|dang nhap|dashboard|upload|export|bao cao)/)
  const hasTone = has(raw, /(chuyen nghiep|than thien|trang trong|ngan gon|thuyet phuc|ky thuat|don gian)/)
  const broadAutomationTopic = kind === 'AUTOMATION' && has(raw, /(cong nghe|tin tuc|kien thuc|thi truong|ai|kinh doanh)/)

  if (clean.length < 32) {
    add(
      'goal-detail',
      'Kết quả cuối cùng bạn muốn iDONE giao cho bạn là gì?',
      'Yêu cầu hiện còn ngắn nên có thể hiểu theo nhiều cách.',
      kind === 'BUILD'
        ? 'Một phiên bản chạy được, ưu tiên 3–5 chức năng cốt lõi và có link để thử.'
        : kind === 'AUTOMATION'
          ? 'Một báo cáo định kỳ ngắn gọn, có các điểm quan trọng, nguồn và đề xuất hành động.'
          : 'Một đầu ra hoàn chỉnh có thể dùng ngay, kèm kết luận và bước tiếp theo.',
    )
  }

  if (kind === 'AUTOMATION') {
    if (broadAutomationTopic || !hasDepth) {
      add(
        'automation-scope',
        'Nhân sự AI nên tập trung vào phạm vi nội dung nào?',
        'Phạm vi quá rộng có thể tạo nhiều thông tin nhưng ít hữu ích.',
        'Ưu tiên AI, phần mềm, tự động hóa, sản phẩm số và startup công nghệ; chỉ chọn nội dung có tác động thực tiễn.',
      )
    }
    if (!hasExplicitFormat || !hasDepth) {
      add(
        'automation-report',
        'Mỗi báo cáo nên ngắn/dài và trình bày theo cách nào?',
        'Định dạng quyết định việc bạn có đọc và hành động được nhanh hay không.',
        '5–7 mục quan trọng; mỗi mục gồm tóm tắt 2–3 câu, nguồn/link, vì sao đáng chú ý và 1 gợi ý ứng dụng.',
      )
    }
    const sourcePreferenceIsMaterial = has(raw, /(y te|suc khoe|phap luat|tai chinh|dau tu|chinh sach|thuoc|benh)/)
    if (!hasSources && sourcePreferenceIsMaterial) {
      add(
        'automation-sources',
        'iDONE nên ưu tiên nguồn và ngôn ngữ nào?',
        'Với chủ đề có rủi ro cao, nguồn ảnh hưởng trực tiếp đến độ tin cậy của báo cáo.',
        'Dùng nguồn gốc/chính thống trước, đối chiếu nguồn uy tín khác và báo cáo bằng ngôn ngữ của người dùng.',
      )
    }
    if (!scheduleHint) {
      add(
        'automation-schedule',
        'Bạn muốn nhân sự AI chạy vào thời điểm nào?',
        'Cần lịch rõ để automation chạy đúng kỳ vọng.',
        'Chạy mỗi ngày lúc 08:00 theo múi giờ của bạn.',
      )
    }
  }

  if (kind === 'RESEARCH' || kind === 'ANALYSIS') {
    if (!hasGeo) add('research-scope','Phạm vi thị trường/địa lý nào cần ưu tiên?','Khác thị trường có thể dẫn đến dữ liệu và kết luận khác nhau.','Bắt đầu với Việt Nam; chỉ mở rộng quốc tế khi cần benchmark.')
    if (!hasTimeWindow && has(raw, /(su kien|thi truong|doi thu|xu huong|chinh sach)/)) add('research-time','Khoảng thời gian nào quan trọng nhất?','Mốc thời gian ảnh hưởng trực tiếp đến độ mới và tính liên quan.','Ưu tiên dữ liệu hiện tại và 6–12 tháng gần nhất; dùng dữ liệu cũ hơn chỉ để so sánh xu hướng.')
    if (!hasDepth) add('research-depth','Bạn muốn mức độ sâu của kết quả đến đâu?','Mức độ chi tiết quyết định số nguồn, thời gian và cách trình bày.','Bản điều hành: kết luận trước, 5–10 phát hiện chính, nguồn, rủi ro và đề xuất hành động.')
  }

  if (kind === 'PLANNING') {
    if (!hasTimeWindow) add('plan-horizon','Kế hoạch cần bao phủ thời gian bao lâu?','Thời hạn quyết định thứ tự ưu tiên và nguồn lực.','Dùng lộ trình 90 ngày, chia theo tuần và có mốc kiểm tra cuối mỗi tháng.')
    if (!hasConstraints) add('plan-constraints','Có giới hạn quan trọng nào về người, tiền hoặc thời gian?','Thiếu ràng buộc có thể làm kế hoạch thiếu thực tế.','Ưu tiên nguồn lực hiện có, hạn chế chi phí mới và chỉ thêm nguồn lực khi có ROI rõ.')
    if (!hasDepth) add('plan-output','Bạn muốn nhận kế hoạch ở mức điều hành hay mức thực thi chi tiết?','Độ chi tiết ảnh hưởng trực tiếp đến khả năng giao việc.','Kế hoạch thực thi gồm ưu tiên, người phụ trách, deadline, KPI và bước tiếp theo.')
  }

  if (kind === 'BUILD') {
    if (!hasFeatures) add('build-must-have','3–5 chức năng bắt buộc của phiên bản này là gì?','Thiếu phạm vi chức năng dễ làm sản phẩm phình to.','Chỉ giữ luồng cốt lõi giúp người dùng đạt kết quả đầu tiên; mọi tính năng khác đưa vào backlog.')
    if (!hasAudience) add('build-user','Ai là người dùng chính của phiên bản này?','Người dùng chính quyết định UX và tiêu chí nghiệm thu.','Chọn một persona chính duy nhất cho MVP; tối ưu toàn bộ luồng cho persona đó.')
    if (!hasConstraints) add('build-constraint','Ràng buộc quan trọng nhất khi build là gì?','Chi phí, nền tảng và thời hạn có thể thay đổi kiến trúc.','Ưu tiên zero-new-spend, web responsive và tận dụng hạ tầng hiện có.')
  }

  if (kind === 'WRITING' || kind === 'COMMUNICATION') {
    if (!hasAudience) add('writing-audience','Nội dung này dành cho ai?','Đối tượng quyết định cách dùng từ và mức chi tiết.','Viết cho người ra quyết định bận rộn: rõ ý, ít thuật ngữ, ưu tiên hành động.')
    if (!hasTone) add('writing-tone','Bạn muốn giọng điệu như thế nào?','Giọng điệu có thể làm thay đổi cách người nhận hiểu thông điệp.','Chuyên nghiệp, rõ ràng, tự nhiên và không khoa trương.')
    if (!hasExplicitFormat) add('writing-format','Bạn muốn đầu ra ở định dạng nào?','Định dạng giúp iDONE tạo đúng sản phẩm ngay từ đầu.','Dùng bản hoàn chỉnh có thể copy/paste ngay; thêm tiêu đề và CTA nếu phù hợp.')
  }

  if (kind === 'DATA') {
    if (!hasExplicitFormat) add('data-format','Bạn muốn nhận dữ liệu ở định dạng nào?','Định dạng ảnh hưởng đến cách xử lý và kiểm tra.','Ưu tiên Excel/XLSX với sheet dữ liệu gốc, sheet tổng hợp và cột kiểm tra.')
    if (!hasDepth) add('data-insight','Bạn cần chỉ làm sạch dữ liệu hay cả phân tích/kết luận?','Hai phạm vi này khác nhau đáng kể về đầu ra.','Làm sạch + kiểm tra + phân tích các chỉ số chính + tóm tắt phát hiện.')
    if (!hasConstraints) add('data-rules','Có quy tắc/công thức nào bắt buộc phải giữ nguyên không?','Sai quy tắc nghiệp vụ có thể làm kết quả sai dù dữ liệu sạch.','Giữ nguyên dữ liệu gốc; mọi công thức mới phải minh bạch và có cột kiểm tra.')
  }

  if (kind === 'GENERAL') {
    add('fallback-outcome','Nếu chỉ được chọn một kết quả quan trọng nhất, bạn muốn iDONE tạo ra điều gì?','Giúp iDONE tối ưu cho kết quả thay vì làm nhiều thứ nhưng không đúng trọng tâm.','Chọn một đầu ra duy nhất có thể dùng ngay và coi các phần còn lại là hỗ trợ.')
    add('fallback-priority','Điều gì quan trọng hơn: nhanh, sâu hay tối ưu chi phí?','Ưu tiên khác nhau sẽ dẫn tới cách thực hiện khác nhau.','Ưu tiên đúng và đủ trước, sau đó tối ưu tốc độ; tránh phát sinh chi phí mới nếu chưa cần.')
    add('fallback-format','Bạn muốn xem kết quả theo cách nào để dễ quyết định nhất?','Cách trình bày ảnh hưởng trực tiếp đến khả năng sử dụng kết quả.','Kết luận ngắn ở đầu, nội dung chính ở giữa, nguồn/rủi ro và bước tiếp theo ở cuối.')
  }

  const criticalIds = new Set(['goal-detail','automation-report','automation-sources','automation-schedule','build-user','research-scope','plan-horizon'])
  const isMateriallyAmbiguous =
    candidates.length > 0 &&
    (
      clean.length < 32 ||
      candidates.length >= 2 ||
      candidates.some(item => criticalIds.has(item.id)) ||
      kind === 'GENERAL'
    )

  return isMateriallyAmbiguous ? candidates.slice(0, 3) : []
}
function inferScheduleHint(raw: string) {
  const t = norm(raw)
  const atHour = t.match(/(?:luc|vao|moi|mỗi)?\s*(\d{1,2})(?::(\d{2}))?\s*(?:gio|h)\s*(sang|chieu|toi)?/)
  if (atHour) {
    let hour = Number(atHour[1])
    const minute = Number(atHour[2] ?? '0')
    const daypart = atHour[3]
    if ((daypart === 'chieu' || daypart === 'toi') && hour < 12) hour += 12
    return `Hàng ngày lúc ${String(hour).padStart(2,'0')}:${String(minute).padStart(2,'0')}`
  }
  if (/hang ngay|moi ngay|daily/.test(t)) return 'Hàng ngày'
  if (/hang tuan|moi tuan|weekly/.test(t)) return 'Hàng tuần'
  return null
}

export function analyzeIntent(raw: string): IntentAnalysis {
  const text = raw.trim()
  const kind = inferKind(text)
  const expectedOutput = inferOutput(text, kind)
  const audience = inferAudience(text)
  const isRecurring = kind === 'AUTOMATION' || has(text, /(hang ngay|moi ngay|hang tuan|moi tuan|dinh ky|daily|weekly|moi .* gio)/)
  const scheduleHint = isRecurring ? inferScheduleHint(text) : null
  const needsFreshInfo = has(text, /(moi nhat|hien tai|hom nay|hang ngay|moi ngay|thi truong|doi thu|su kien|chinh sach|gia|tin tuc|cap nhat|cong nghe)/)
  const needsExternalAction = has(text, /(gui email|gui tin|dang bai|xoa|thanh toan|mua|dat cho|moi hop|cam ket)/)
  const questions = inferQuestions(text, kind, scheduleHint)
  const confidence = Math.max(0.55, Math.min(0.96, 0.88 - questions.length * 0.08 + (text.length > 80 ? 0.05 : 0)))

  const assumptions = [
    'iDONE ưu tiên hoàn thành đầu ra thay vì yêu cầu người dùng hiểu quy trình nội bộ.',
    needsFreshInfo
      ? 'Dữ liệu thay đổi theo thời gian phải được kiểm tra từ nguồn hiện hành.'
      : 'Chỉ sử dụng dữ liệu có nguồn hoặc dữ liệu người dùng cung cấp.',
  ]
  if (needsExternalAction) assumptions.push('Mọi hành động bên ngoài hệ thống cần người dùng duyệt trước khi thực hiện.')

  const outOfScope = [
    'Không tự bịa dữ liệu còn thiếu.',
    'Không coi kết quả AI tự khẳng định là bằng chứng hoàn thành.',
  ]
  if (needsExternalAction) outOfScope.push('Không tự động thực hiện hành động bên ngoài khi chưa có duyệt rõ ràng.')

  const scope = `Hoàn thành yêu cầu: ${text}`
  const data = needsFreshInfo
    ? 'Dữ liệu người dùng cung cấp + nguồn hiện hành được phép truy cập và kiểm chứng.'
    : 'Dữ liệu người dùng cung cấp + nguồn có thể kiểm chứng khi cần.'
  const constraints = [
    'Không bịa dữ liệu.',
    'Chỉ hỏi khi thiếu thông tin có thể làm thay đổi đáng kể kết quả.',
    'Nếu còn mơ hồ đáng kể, hỏi tối đa 3 câu theo thứ tự giá trị; dừng ngay khi đã đủ thông tin. Mỗi câu luôn có gợi ý và lựa chọn nhanh.',
    needsExternalAction ? 'Hành động bên ngoài phải được duyệt trước.' : 'Không phát sinh hành động bên ngoài ngoài phạm vi yêu cầu.',
  ].join(' ')

  const routeSteps = isRecurring ? [
    'Hiểu nhiệm vụ lặp lại và đầu ra mỗi lần chạy.',
    scheduleHint ? `Xác nhận lịch: ${scheduleHint}.` : 'Xác định lịch chạy.',
    needsFreshInfo ? 'Mỗi lần chạy, thu thập và kiểm tra dữ liệu hiện hành.' : 'Mỗi lần chạy, tổng hợp dữ liệu cần thiết.',
    'Tạo báo cáo/kết quả theo định dạng mong đợi.',
    'Kiểm tra chất lượng trước khi lưu kết quả.',
    'Lưu lịch sử các lần chạy và chỉ yêu cầu duyệt nếu có hành động bên ngoài.',
  ] : [
    'Hiểu ý định và xác định đầu ra mong đợi.',
    questions.length ? 'Làm rõ các thiếu hụt có tác động cao.' : 'Xác nhận đủ thông tin để bắt đầu.',
    needsFreshInfo ? 'Thu thập và kiểm tra dữ liệu hiện hành.' : 'Tổng hợp dữ liệu và bối cảnh cần thiết.',
    kind === 'BUILD' ? 'Tạo hoặc chỉnh sửa sản phẩm/chức năng.' : 'Tạo kết quả theo định dạng phù hợp.',
    'Kiểm tra tính đúng, đủ và các tiêu chí nghiệm thu.',
    'Trình bày kết quả ngắn gọn và đưa phần kỹ thuật vào lớp kiểm chứng phía sau.',
  ]

  return {
    kind,
    confidence,
    title: inferTitle(text, kind),
    goal: text,
    audience,
    expectedOutput,
    needsFreshInfo,
    needsExternalAction,
    isRecurring,
    scheduleHint,
    questions,
    summary: {
      understood: [
        `Mục tiêu: ${text}`,
        `Đối tượng: ${audience}`,
        `Đầu ra: ${expectedOutput}`,
      ],
      assumptions,
      outOfScope,
      expectedOutput,
    },
    workSpec: {
      goal: text,
      audience,
      scope,
      data,
      constraints,
      output: expectedOutput,
      acceptance: 'Kết quả phải đúng yêu cầu, đủ nội dung bắt buộc, có bằng chứng phù hợp và không còn lỗi nghiêm trọng.',
      dependencies: isRecurring
        ? 'Runtime thực thi AI + scheduler nội bộ + nguồn dữ liệu phù hợp.'
        : needsFreshInfo ? 'Khả năng truy cập nguồn dữ liệu hiện hành và công cụ thực thi phù hợp.' : 'Công cụ thực thi phù hợp với loại công việc.',
      assumptions: assumptions.map(item => `Cần xác nhận — ${item}`).join('\n'),
      unknowns: questions.map(item => `${item.question} — Gợi ý: ${item.suggestion}`).join('\n'),
      approvals: needsExternalAction ? 'Cần duyệt rõ ràng trước hành động bên ngoài.' : 'Chỉ cần duyệt ở điểm DONE hoặc khi vượt ranh giới đã được phê duyệt.',
      outOfScope: outOfScope.join('\n'),
    },
    routePlan: {
      steps: routeSteps,
      tools:
        isRecurring
          ? 'iDONE Executor + Scheduler'
          : kind === 'BUILD'
            ? 'Chat / Work / Codex'
            : needsFreshInfo
              ? 'Chat / Work / Web'
              : 'Chat / Work',
      checkpoints: 'D-033; D-032; Acceptance; DoD',
    },
  }
}
